import dayjs, { type Dayjs } from "dayjs";
import { getGoodsPricing } from "@/utils/goodsPricing";
import { isNonNegativeNumber as validNumber } from "../numericValidation";
import type { GoodsDetail, GoodsRow, GoodsSku, GoodsWrite } from "@/types/shopGoods";

export interface GoodsFormValues extends Omit<GoodsWrite, "timingShelfTime" | "preSaleTime"> {
  timingShelfTime?: Dayjs | null;
  preSaleTime?: Dayjs | null;
}

export function canShelfGoods(auditStatus?: number, goodsType?: number): boolean {
  return goodsType === 1 || auditStatus === 2;
}

export function canChangeGoodsShelf(row: Pick<GoodsRow, "auditStatus" | "isShelf"> & Partial<Pick<GoodsRow, "goodsType">>): boolean {
  return (row.goodsType === 1 || row.auditStatus !== 1) &&
    (row.isShelf === 1 || (row.isShelf === 0 && canShelfGoods(row.auditStatus, row.goodsType)));
}

export function validateGoodsShelf(values: GoodsFormValues, auditStatus?: number): void {
  if (!canShelfGoods(auditStatus, values.goodsType) && values.isShelf === 1)
    throw new Error("商品未通过审核，不能立即上架，请选择下架后保存");
}

export function emptySku(): GoodsSku {
  return { skuName: "默认规格", price: 0, marketPrice: 0, stock: 0, sort: 0 };
}
function writableSku(sku: GoodsSku): GoodsSku {
  return {
    ...(sku.id != null ? { id: sku.id } : {}),
    skuName: sku.skuName.trim(),
    price: Number(sku.price),
    stock: Number(sku.stock),
    ...(sku.skuSpecValueIds != null
      ? {
          skuSpecValueIds: sku.skuSpecValueIds
            .split(",")
            .map((value) => value.trim())
            .join(","),
        }
      : {}),
    ...(sku.skuCode !== undefined ? { skuCode: sku.skuCode } : {}),
    ...(sku.barCode !== undefined ? { barCode: sku.barCode } : {}),
    ...(sku.marketPrice !== undefined ? { marketPrice: sku.marketPrice } : {}),
    ...(sku.costPrice !== undefined ? { costPrice: sku.costPrice } : {}),
    ...(sku.stockWarn !== undefined ? { stockWarn: sku.stockWarn } : {}),
    ...(sku.weight !== undefined ? { weight: sku.weight } : {}),
    ...(sku.volume !== undefined ? { volume: sku.volume } : {}),
    ...(sku.skuImage !== undefined ? { skuImage: sku.skuImage } : {}),
    ...(sku.sort !== undefined ? { sort: sku.sort } : {}),
  };
}
export function goodsFormValues(detail?: GoodsDetail): GoodsFormValues {
  if (!detail)
    return {
      goodsName: "",
      mainImage: "",
      goodsType: 1,
      stockType: 1,
      logisticsType: 1,
      isSevenReturn: 0,
      aftersaleType: 0,
      isShelf: 0,
      isTimingShelf: 0,
      isMultiSpecification: 0,
      supplyType: 1,
      sort: 0,
      slideList: [],
      specRelList: [],
      categoryIds: [],
      skuList: [emptySku()],
    };
  return {
    id: detail.id,
    goodsName: detail.goodsName,
    mainImage: detail.mainImage,
    goodsSubtitle: detail.goodsSubtitle,
    goodsDesc: detail.goodsDesc,
    goodsType: detail.goodsType,
    stockType: detail.stockType,
    preSaleNote: detail.preSaleNote,
    preSaleTime: detail.preSaleTime ? dayjs(detail.preSaleTime) : null,
    logisticsType: detail.logisticsType,
    postage: detail.postage,
    remoteAreaIds: (detail.remoteAreaIds ?? detail.remoteAreaList?.map((area) => area.id) ?? []).map(String),
    isSevenReturn: 0,
    aftersaleType: 0,
    // 编辑保存会重新进入待审核并下架，上架由独立接口完成。
    isShelf: 0,
    isTimingShelf: 0,
    timingShelfTime: null,
    isMultiSpecification: detail.isMultiSpecification,
    supplyType: detail.supplyType,
    supplierId: detail.supplierId,
    selfSupplier: detail.goodsType === 1 ? detail.selfSupplier : undefined,
    sort: detail.sort,
    price: detail.price,
    stock: detail.stock,
    marketPrice: detail.marketPrice,
    slideList: (detail.slideList ?? []).map((slide) => ({
      slideImage: slide.slideImage,
      sort: slide.sort ?? 0,
    })),
    specRelList: (detail.specRelList ?? []).map((spec) => ({
      specId: spec.specId,
      ...(spec.specName ? { specName: spec.specName } : {}),
      sort: spec.sort ?? 0,
    })),
    categoryIds: (detail.categoryRelList ?? []).map((item) => item.categoryId),
    skuList: detail.skuList?.length
      ? detail.skuList.map(writableSku)
      : [
          {
            ...emptySku(),
            price: detail.price,
            stock: detail.stock,
            marketPrice: detail.marketPrice,
          },
        ],
  };
}
export function goodsPayload(values: GoodsFormValues, originalSkus: GoodsSku[] = []): GoodsWrite {
  // 暂停七天无理由和定时上架，保存时关闭历史配置。
  values = { ...values, isSevenReturn: 0, aftersaleType: 0, isTimingShelf: 0, timingShelfTime: null };
  if (!values.goodsName?.trim() || !values.mainImage?.trim())
    throw new Error("请填写商品名称和主图地址");
  if (
    ![1, 2].includes(values.goodsType) ||
    ![1, 2].includes(values.stockType) ||
    ![0, 1].includes(values.isShelf) ||
    ![0, 1, 2].includes(values.logisticsType) ||
    ![0, 1].includes(values.isMultiSpecification)
  ) {
    throw new Error("请选择库存类型、上架设置、物流类型和规格模式");
  }
  if (values.goodsType === 1 && !values.selfSupplier?.trim())
    throw new Error("请填写自营供应商");
  if (values.stockType === 2 && !values.preSaleTime?.isValid())
    throw new Error("请选择预售结束/可开始发货时间");
  if (values.isTimingShelf === 1 && !values.timingShelfTime?.isValid())
    throw new Error("请选择定时上架时间");
  if ((values.logisticsType === 0 || values.logisticsType === 2) && !validNumber(values.postage))
    throw new Error("邮费必须为大于或等于 0 的有效数字");
  if (values.sort != null && !validNumber(values.sort, true))
    throw new Error("商品排序必须为非负整数");
  if (values.slideList.some((slide) => slide.sort != null && !validNumber(slide.sort, true)))
    throw new Error("轮播图排序必须为非负整数");
  if (values.isMultiSpecification === 1 && values.specRelList.some((spec) => spec.sort != null && !validNumber(spec.sort, true)))
    throw new Error("规格项排序必须为非负整数");
  if (
    values.goodsType === 1 &&
    values.supplyType === 2 &&
    !/^[1-9]\d*$/.test(String(values.supplierId ?? ""))
  )
    throw new Error("请输入有效供应商ID");
  const multi = values.isMultiSpecification === 1;
  const skus = (multi ? values.skuList : values.skuList.slice(0, 1)).map((sku) => {
    // 按 ID 合并详情中的可写字段，新增行不继承旧 SKU；显式 null 保留为清空。
    const original = sku.id == null ? undefined : originalSkus.find((item) => String(item.id) === String(sku.id));
    return { ...original, ...sku };
  });
  if (!skus.length) throw new Error("请至少配置一个 SKU");
  for (const sku of skus) {
    if (!validNumber(sku.price) || Number(sku.price) <= 0) throw new Error("销售价格必须大于0");
    if (!validNumber(sku.costPrice)) throw new Error("请填写每个 SKU 的结算价，且不能为负数");
    if (!sku.skuImage?.trim()) throw new Error("请上传 SKU 图片");
    if (
      !sku.skuName?.trim() ||
      !validNumber(sku.price) ||
      !validNumber(sku.stock, true)
    ) {
      throw new Error("请填写每个 SKU 的名称、有效价格和整数库存");
    }
    for (const amount of [
      sku.marketPrice,
      sku.costPrice,
      sku.weight,
      sku.volume,
    ]) {
      if (amount != null && !validNumber(amount))
        throw new Error("SKU 金额、重量和体积不能为负数");
    }
    if (sku.stockWarn != null && !validNumber(sku.stockWarn, true))
      throw new Error("库存预警必须为非负整数");
    if (sku.sort != null && !validNumber(sku.sort, true))
      throw new Error("SKU 排序必须为非负整数");
  }
  const categoryIds = Array.from(new Set((values.categoryIds ?? []).map((id) => String(id).trim())));
  if (categoryIds.some((id) => !/^[1-9]\d*$/.test(id)))
    throw new Error("请选择有效的商品分类");
  const specIds = values.specRelList.map((spec) => String(spec.specId).trim());
  if (multi) {
    if (!specIds.length) throw new Error("请至少添加一个规格项");
    const invalidSpec = specIds.findIndex((id) => !/^[1-9]\d*$/.test(id));
    if (invalidSpec !== -1) throw new Error(`请选择第 ${invalidSpec + 1} 个规格项`);
    if (new Set(specIds).size !== specIds.length) throw new Error("规格项不能重复添加");
    const combinations = new Set<string>();
    for (const sku of skus) {
      const ids = (sku.skuSpecValueIds ?? "").split(",").map((id) => id.trim());
      if (
        ids.length !== specIds.length ||
        ids.some((id) => !/^[1-9]\d*$/.test(id)) ||
        new Set(ids).size !== ids.length
      ) {
        throw new Error(
          "请为每个 SKU 选择完整的规格组合"
        );
      }
      const combination = [...ids].sort().join(",");
      if (combinations.has(combination))
        throw new Error("SKU 规格组合不能重复");
      combinations.add(combination);
    }
  }
  if (values.slideList.some((slide) => !slide.slideImage?.trim()))
    throw new Error("请填写轮播图地址或移除空白项");
  const firstSku = skus[0];
  const { timingShelfTime, preSaleTime, selfSupplier, ...data } = values;
  return {
    ...data,
    goodsType: values.goodsType,
    ...(values.goodsType === 1 ? { selfSupplier: selfSupplier?.trim() } : {}),
    postage: values.logisticsType === 1 ? 0 : values.postage,
    remoteAreaIds: values.remoteAreaIds ?? [],
    supplyType: values.goodsType === 2 ? 1 : values.supplyType,
    supplierId:
      values.goodsType === 1 && values.supplyType === 2
        ? values.supplierId
        : null,
    categoryIds: categoryIds.map((id) => Number.isSafeInteger(Number(id)) ? Number(id) : id),
    goodsName: values.goodsName.trim(),
    mainImage: values.mainImage.trim(),
    preSaleTime:
      values.stockType === 2 && preSaleTime
        ? preSaleTime.format("YYYY-MM-DD HH:mm:ss")
        : null,
    timingShelfTime:
      values.isTimingShelf === 1 && timingShelfTime
        ? timingShelfTime.format("YYYY-MM-DD HH:mm:ss")
        : null,
    ...(!multi && firstSku
      ? {
          price: firstSku.price,
          stock: firstSku.stock,
          marketPrice: firstSku.marketPrice ?? null,
        }
      : getGoodsPricing({ isMultiSpecification: 1, skuList: skus })),
    slideList: values.slideList.map((slide) => ({
      slideImage: slide.slideImage.trim(),
      sort: slide.sort ?? 0,
    })),
    specRelList: multi
      ? values.specRelList.map((spec) => ({
          specId: typeof spec.specId === "string" ? spec.specId.trim() : spec.specId,
          sort: spec.sort ?? 0,
        }))
      : [],
    skuList: skus.map((sku) => ({
      ...writableSku(sku),
      ...(!multi ? { skuSpecValueIds: "" } : {}),
    })),
  };
}
